import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import WIcon from '../shared/WIcon';

const PALETTE = ['#3b82f6', '#00bf63', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981', '#0ea5e9'];

const initialsFor = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();

const sizeOf = (count) => {
  const n = parseInt(count, 10) || 0;
  if (n >= 250) return { key: 'large',  label: 'Large',  color: '#8b5cf6', bg: 'rgba(139,92,246,0.10)' };
  if (n >= 50)  return { key: 'medium', label: 'Medium', color: '#3b82f6', bg: 'rgba(59,130,246,0.10)' };
  return            { key: 'small',  label: 'Small',  color: '#10b981', bg: 'rgba(16,185,129,0.10)' };
};

const SORT_OPTIONS = [
  { id: 'name', label: 'Company name' },
  { id: 'size', label: 'Largest first' },
];

const CompanySelector = ({
  selectedCompanies = [],
  onCompaniesChange,
  maxSelections = 100,
}) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const snap = await getDocs(query(collection(db, 'companies'), orderBy('name'), limit(200)));
        if (cancelled) return;
        setCompanies(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error loading companies:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selectedIds = useMemo(() => new Set(selectedCompanies.map(c => c.id)), [selectedCompanies]);
  const totalEmployees = useMemo(
    () => selectedCompanies.reduce((sum, c) => sum + (parseInt(c.employeeCount, 10) || 0), 0),
    [selectedCompanies],
  );

  const visible = useMemo(() => {
    let list = companies;
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((c) => (c.name || '').toLowerCase().includes(s));
    }
    list = [...list].sort((a, b) => {
      if (sortBy === 'size') {
        return (parseInt(b.employeeCount, 10) || 0) - (parseInt(a.employeeCount, 10) || 0);
      }
      return (a.name || '').localeCompare(b.name || '');
    });
    return list;
  }, [companies, search, sortBy]);

  const toggle = (company) => {
    if (selectedIds.has(company.id)) {
      onCompaniesChange?.(selectedCompanies.filter((c) => c.id !== company.id));
    } else {
      if (selectedCompanies.length >= maxSelections) return;
      onCompaniesChange?.([...selectedCompanies, company]);
    }
  };

  const selectAll = () => {
    const merged = [...selectedCompanies];
    const existing = new Set(selectedIds);
    for (const c of visible) {
      if (!existing.has(c.id) && merged.length < maxSelections) {
        merged.push(c);
        existing.add(c.id);
      }
    }
    onCompaniesChange?.(merged);
  };

  const clearAll = () => onCompaniesChange?.([]);

  const allVisibleSelected = visible.length > 0 && visible.every((c) => selectedIds.has(c.id));

  return (
    <div style={{
      border: '1.5px solid #e2e8f0',
      borderRadius: 10,
      background: 'white',
      overflow: 'hidden',
      fontFamily: 'inherit',
    }}>
      {/* Toolbar */}
      <div style={{
        padding: '10px 12px',
        borderBottom: '1px solid #f1f5f9',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        background: '#f8fafc',
      }}>
        <div style={{
          flex: 1, minWidth: 180,
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'white',
          border: '1.5px solid #e2e8f0',
          borderRadius: 7,
          padding: '6px 10px',
        }}>
          <WIcon name="search" size={14} color="#94a3b8" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search companies…"
            style={{ border: 'none', background: 'none', fontSize: 13, fontFamily: 'inherit', color: '#0f172a', outline: 'none', width: '100%' }}
          />
        </div>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{
            padding: '7px 10px', borderRadius: 7,
            border: '1.5px solid #e2e8f0', background: 'white',
            color: '#334155', fontSize: 12, fontWeight: 600,
            fontFamily: 'inherit', cursor: 'pointer', outline: 'none',
          }}
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>

        <button
          type="button"
          onClick={selectAll}
          disabled={visible.length === 0 || allVisibleSelected}
          style={{
            padding: '7px 12px', borderRadius: 7,
            border: '1.5px solid #e2e8f0',
            background: 'white',
            color: allVisibleSelected ? '#94a3b8' : '#334155',
            fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
            cursor: visible.length === 0 || allVisibleSelected ? 'not-allowed' : 'pointer',
            opacity: visible.length === 0 || allVisibleSelected ? 0.6 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          Select all
        </button>

        {selectedCompanies.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            style={{
              padding: '7px 12px', borderRadius: 7,
              border: 'none',
              background: 'rgba(239,68,68,0.10)',
              color: '#dc2626',
              fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Clear ({selectedCompanies.length})
          </button>
        )}
      </div>

      {/* Selection summary */}
      <div style={{
        padding: '10px 14px',
        background: selectedCompanies.length > 0 ? 'rgba(0,191,99,0.06)' : '#fafbfc',
        borderBottom: '1px solid #f1f5f9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        fontSize: 12,
      }}>
        <span style={{ color: '#475569', fontWeight: 600 }}>
          {selectedCompanies.length === 0
            ? 'No companies selected — pick the ones to target with this offer.'
            : `${selectedCompanies.length} of ${companies.length} ${selectedCompanies.length === 1 ? 'company' : 'companies'} selected`}
        </span>
        {selectedCompanies.length > 0 && (
          <span style={{
            padding: '3px 10px',
            borderRadius: 999,
            background: 'rgba(0,191,99,0.12)',
            color: '#00bf63',
            fontWeight: 700,
            fontSize: 11,
            whiteSpace: 'nowrap',
          }}>
            ≈ {totalEmployees.toLocaleString()} {totalEmployees === 1 ? 'employee' : 'employees'} reached
          </span>
        )}
      </div>

      {/* List */}
      <div style={{ maxHeight: 280, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            Loading companies…
          </div>
        ) : visible.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            {companies.length === 0 ? 'No companies yet.' : 'No companies match your search.'}
          </div>
        ) : (
          visible.map((company, i) => {
            const checked = selectedIds.has(company.id);
            const size = sizeOf(company.employeeCount);
            const color = PALETTE[i % PALETTE.length];
            return (
              <button
                key={company.id}
                type="button"
                onClick={() => toggle(company)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 14px',
                  border: 'none',
                  borderBottom: '1px solid #f8fafc',
                  background: checked ? 'rgba(0,191,99,0.06)' : 'white',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = '#fafbfc'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = checked ? 'rgba(0,191,99,0.06)' : 'white'; }}
              >
                <div style={{
                  width: 18, height: 18, borderRadius: 5,
                  border: `2px solid ${checked ? '#00bf63' : '#cbd5e1'}`,
                  background: checked ? '#00bf63' : 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'all 0.15s',
                }}>
                  {checked && (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>

                {company.logoUrl ? (
                  <img
                    src={company.logoUrl}
                    alt={company.name}
                    style={{ width: 28, height: 28, borderRadius: 7, objectFit: 'cover', flexShrink: 0 }}
                  />
                ) : (
                  <div style={{
                    width: 28, height: 28, borderRadius: 7,
                    background: color + '20', color, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 10, fontWeight: 700,
                  }}>{initialsFor(company.name)}</div>
                )}

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 600, color: '#0f172a',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{company.name || 'Unnamed'}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                    {company.employeeCount || 0} {company.employeeCount === 1 ? 'employee' : 'employees'}
                  </div>
                </div>

                <span style={{
                  padding: '3px 9px',
                  borderRadius: 999,
                  background: size.bg,
                  color: size.color,
                  fontSize: 11,
                  fontWeight: 700,
                  flexShrink: 0,
                }}>
                  {size.label}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CompanySelector;
